import * as Yup from 'yup';
import moment from 'moment-timezone';

import { Nullable } from '~globals/types/commons';
import { ScheduleRouteRepetitionType } from '~globals/types/enums';
import {
  ScheduleRouteWeeklyDayIndex,
  ScheduleRouteBase,
} from '~services/scheduleRoute/types';
import { VALIDATIONS_ERROR } from '~constants/validationsError';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';
import { isRouteScheduleRepetionTypeWeekly } from '~utils/scheduleRoute/repetionType';
import { sortWeeklyDays } from '~utils/scheduleRoute/repetionDescription/helpers';
import { FormatterDateUtcOffset } from '~hooks/useUtcOffset';

import { validateEndDateIsAftertartDate } from './helpers';

export const SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME = {
  SCHEDULE_ROUTE_ENABLED: 'scheduleRouteEnabled',
  SCHEDULE_ROUTE_REPETION_TYPE: 'scheduleRouteRepetionType',
  SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT: 'scheduleRouteRepetionIntervalCount',
  SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS: 'scheduleRouteRepetionIntervalDays',
  SCHEDULE_ROUTE_END_DATE: 'scheduleRouteEndDate',
} as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getScheduleRouteBaseFormInititalValues = () => ({
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED]: false,
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE]:
    ScheduleRouteRepetitionType.Weekly as ScheduleRouteRepetitionType,
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT]: 1,
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS]:
    [] as ScheduleRouteWeeklyDayIndex[],
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE]:
    null as Nullable<Date>,
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getScheduleRouteBaseValidationObject = (startDateKey: string) => ({
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED]: Yup.boolean(),
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE]:
    Yup.number().when(
      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED,
      {
        is: true,
        then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT]:
    Yup.number().when(
      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED,
      {
        is: true,
        then: (schema) =>
          schema
            .moreThan(0, VALIDATIONS_ERROR.MORE_THAN(0))
            .required(VALIDATIONS_ERROR.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS]:
    Yup.array()
      .of(Yup.number())
      .when(
        [
          SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED,
          SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE,
        ],
        {
          is: (
            scheduleRouteEnabled: boolean,
            scheduleRouteRepetionType: ScheduleRouteRepetitionType,
          ) =>
            scheduleRouteEnabled &&
            isRouteScheduleRepetionTypeWeekly(scheduleRouteRepetionType),
          then: (schema) => schema.min(1, 'Debe seleccionar al menos uno'),
        },
      ),
  [SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE]: Yup.date()
    .nullable()
    .when(SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED, {
      is: true,
      then: (schema) =>
        schema
          .typeError(VALIDATIONS_ERROR.INVALID_DATE)
          .test(
            'is-after-start-date',
            'Debe ser mayor que fecha de agenda/inicio',
            (currentScheduleRouteEndDate, context) => {
              const currentScheduleStartDate: Nullable<Date> =
                context.parent[startDateKey];

              if (currentScheduleStartDate && currentScheduleRouteEndDate) {
                return validateEndDateIsAftertartDate(
                  currentScheduleStartDate,
                  currentScheduleRouteEndDate,
                );
              }

              return true;
            },
          )
          .required(VALIDATIONS_ERROR.REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const getScheduleRouteBody = ({
  title,
  startDate,
  vehicleId,
  driverId,
  templateId,
  formValues,
  formatterDateUtcOffset,
}: {
  title: string;
  startDate: string;
  vehicleId: string | number;
  driverId: Nullable<string | number>;
  templateId: Nullable<string>;
  formValues: ReturnType<typeof getScheduleRouteBaseFormInititalValues>;
  formatterDateUtcOffset: FormatterDateUtcOffset;
}): ScheduleRouteBase => {
  const endDate = formatterDateUtcOffset(
    moment(
      formValues[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE],
    ).startOf('day'),
    moment.HTML5_FMT.DATETIME_LOCAL_MS,
    true,
  );

  const repetitionTypeId: ScheduleRouteRepetitionType = Number(
    formValues[
      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE
    ],
  );

  const repeatInterval = Number(
    formValues[
      SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME
        .SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT
    ],
  );

  let repetitionIntervalArray: Nullable<string> = null;

  if (isRouteScheduleRepetionTypeWeekly(repetitionTypeId)) {
    repetitionIntervalArray = sortWeeklyDays(
      formValues[
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME
          .SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS
      ],
    ).join(',');
  }

  return {
    routeScheduleId: DEFAULT_UUIID_EMPTY,
    routeTemplateId: templateId,
    startDate,
    endDate,
    title,
    repetitionTypeId,
    repeatInterval,
    repetitionIntervalArray,
    driverId,
    vehicleId,
    createRoutes: true,
    enabled: true,
  };
};
